import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { MdDragIndicator } from 'react-icons/md';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { DefaultTypography } from '@/components/typography';
import { GroupDisableButton } from '@/features/editor/widgets/custom-widget/inputs/shared/group-disable-button';
import { Flexbox } from '@/components/flex';
import { QuickEditContext } from '@/features/editor/widgets/custom-widget/shared/quick-edit-helper';
import { CustomWidgetContext } from '@/features/editor/widgets/custom-widget/shared/context';
import { GenericInput } from '@/features/editor/widgets/custom-widget/inputs';
import { DeviceType } from '@/utils/definitions';
import { MaybeFiltered } from '../../shared/shared-styles';
import { HistoryOpts } from '@/features/editor/widgets/custom-widget/inputs/shared/input-type';

export function QuickEditItem({
  quickEdit,
  device,
  showInputName,
  isAllowToDrag,
  name,
  isOverlayDND,
  setOrderKey,
  isAnimated,
}: {
  quickEdit: QuickEditContext[];
  device: DeviceType;
  showInputName?: boolean;
  isAllowToDrag?: boolean;
  name?: string;
  isOverlayDND?: boolean;
  setOrderKey?: (o: any) => void;
  isAnimated?: boolean;
}) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const {
    updateCustomizationStatus,
    getResponsiveValue,
    setResponsiveValue,
    evaluateRule,
    currentSchema,
    deleteBlock,
  } = useContext(CustomWidgetContext);
  const customization = quickEdit?.[0]?.customization;
  const customizationIdx = quickEdit?.[0]?.customizationIdx;

  const [isDisabled, setIsDisabled] = useState(
    customization.isDisabled || false,
  );
  const [isOpen, setIsOpen] = useState(!isDisabled);

  useEffect(() => {
    const c = currentSchema.customizations[quickEdit?.[0].customizationIdx];
    if (c.isDisabled !== isDisabled) {
      setIsDisabled(c.isDisabled);
      setIsOpen(!c.isDisabled);
    }
  }, [currentSchema.customizations[quickEdit?.[0].customizationIdx]]);

  const onToggle = () => {
    if (isDisabled) return;
    setIsOpen(!isOpen);
  };

  const onDisableSection = (ev) => {
    ev.stopPropagation();
    setIsDisabled(!isDisabled);
    setIsOpen(isDisabled);
    updateCustomizationStatus(customizationIdx, !isDisabled);
  };
  const onDeleteBlock = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const init = () => {
      setIsDisabled(true);
      setIsOpen(false);
      deleteBlock(customizationIdx);
      if (setOrderKey) setOrderKey((prev) => prev + 1);
    };

    if (!nodeRef.current) {
      init();
    } else {
      nodeRef.current.classList.add(`delete`);
      setTimeout(() => {
        nodeRef.current?.classList.remove(`delete`);
        init();
      }, 500 + 100);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = isAllowToDrag
    ? useSortable({ id: name })
    : {
        attributes: {},
        listeners: {},
        setNodeRef: undefined,
        transform: undefined,
        transition: undefined,
        isDragging: false,
      };

  const combinedRef = (el: HTMLDivElement | null) => {
    nodeRef.current = el;
    if (setNodeRef) setNodeRef(el);
  };

  const headerRef = useRef<HTMLDivElement>(null);
  const collapsedHeightRef = useRef<number | null>(
    isAllowToDrag ? null : undefined,
  );

  const getCollapsedHeight = isAllowToDrag
    ? () => {
        if (collapsedHeightRef.current === null) {
          collapsedHeightRef.current =
            (headerRef.current?.offsetHeight || 25) + 30;
        }
        return collapsedHeightRef.current;
      }
    : () => 0;

  const style = useMemo(() => {
    if (!isAllowToDrag || !transform) return {};

    const collapsedHeight = getCollapsedHeight();

    const getYOffset = () => {
      if (transform.y > 0) return collapsedHeight;
      if (transform.y < 0) return -collapsedHeight;
      return transform.y;
    };

    return {
      transform: `translateY(${getYOffset()}px)`,
      transition,
    };
  }, [transform, isAllowToDrag]);

  const quickEditItemReturn = (
    <MaybeFiltered isVisible={!evaluateRule(customization?.hideWhen)}>
      <Wrapper key={customization.key}>
        <HeaderSection ref={headerRef} onClick={onToggle}>
          <Text>{customization.name}</Text>
          <Flexbox direction="row" gap="1rem">
            {customization.canBeDisabled && !isAllowToDrag && (
              <GroupDisableButton
                isDisabled={isDisabled}
                onClick={onDisableSection}
              />
            )}
            {customization.canBeDisabled && isAllowToDrag && (
              <RiDeleteBin5Line
                onClick={(e) => onDeleteBlock(e)}
                size={20}
                color="#a6afb8"
              />
            )}
            {isAllowToDrag && (
              <MdDragIndicator
                {...attributes}
                {...listeners}
                size={20}
                cursor="grab"
                color="#a6afb8"
              />
            )}
            <Text
              style={{
                opacity: isDisabled ? `0` : `1`,
                pointerEvents: isDisabled ? `none` : `auto`,
              }}
            >
              {isOpen ? `-` : `+`}
            </Text>
          </Flexbox>
        </HeaderSection>
        <ContentSection
          isDragging={isDragging}
          isOpen={isOpen && !isOverlayDND}
        >
          {quickEdit.map((qec) => {
            const initialValues = (
              key?: string,
              d?: DeviceType,
              specIdx?: number,
            ): any =>
              getResponsiveValue(
                qec.customizationIdx,
                qec.componentIdx,
                specIdx || qec.specIdx,
                key,
                d || device,
              );

            const onValuesChanged = (
              key: string,
              value: any,
              d?: DeviceType,
              historyOpts?: HistoryOpts,
            ): void => {
              setResponsiveValue(
                qec.customizationIdx,
                qec.componentIdx,
                qec.specIdx,
                key,
                value,
                d || device,
                !!historyOpts?.ignoreHistory,
                !!historyOpts?.forceSameVersion,
              );
            };

            return (
              <MaybeFiltered
                key={`wrapper-${qec.spec.key}`}
                isVisible={
                  !(
                    evaluateRule(qec?.component?.hideWhen) ||
                    evaluateRule(qec?.spec?.hideWhen)
                  )
                }
              >
                <GenericInput
                  key={qec.spec.key}
                  device={device}
                  showInputName={showInputName}
                  customizationIdx={qec.customizationIdx}
                  customization={qec.customization}
                  componentIdx={qec.componentIdx}
                  component={qec.component}
                  specIdx={qec.specIdx}
                  spec={qec.spec}
                  initialValues={initialValues}
                  onValuesChanged={onValuesChanged}
                />
              </MaybeFiltered>
            );
          })}
        </ContentSection>
      </Wrapper>
    </MaybeFiltered>
  );

  if (isOverlayDND) {
    return (
      <DraggingWrapper
        ref={combinedRef}
        className="dragging-slot overlay-dragging"
      >
        <HeaderSection ref={headerRef}>
          <Text>{customization.name}</Text>
          <Flexbox direction="row" gap="1rem">
            <MdDragIndicator
              {...attributes}
              {...listeners}
              size={25}
              cursor="grab"
              color="#a6afb8"
            />
          </Flexbox>
        </HeaderSection>
      </DraggingWrapper>
    );
  }

  return isAllowToDrag ? (
    <DraggingWrapper
      ref={combinedRef}
      className={`dragging-slot ${isDragging ? `dragging` : ``} ${
        isOverlayDND ? `overlay-dragging` : ``
      } ${isAnimated ? `animated` : ``}`}
      style={style}
    >
      {quickEditItemReturn}
    </DraggingWrapper>
  ) : (
    quickEditItemReturn
  );
}

const Wrapper = styled.div<{ isBluePrint?: boolean }>`
  && {
    user-select: none;
    padding: 2rem;
    background: #f4f6f7;
    border-radius: 10px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  cursor: pointer;
`;

const Text = styled(DefaultTypography)`
  color: #818f9c;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.46px;
  text-transform: capitalize;
`;

const ContentSection = styled.div`
  && {
    display: ${(props: { isOpen: boolean }) =>
      props.isOpen ? `flex` : `none`};
    flex-direction: column;
    overflow: ${(props: { isOpen: boolean }) =>
      props.isOpen ? `visible` : `hidden`};
    max-height: ${(props: { isDragging: boolean }) =>
      props.isDragging ? `0` : `10000px`};
    transition: max-height 30ms linear;
    && > * {
      margin-top: 1.5rem;
    }
  }
`;

const DraggingWrapper = styled.div`
  &.overlay-dragging {
    box-shadow: rgba(0, 0, 0, 0.15) 0 54px 55px,
      rgba(0, 0, 0, 0.06) 0 -12px 30px, rgba(0, 0, 0, 0.06) 0 4px 6px,
      rgba(0, 0, 0, 0.09) 0 12px 13px, rgba(0, 0, 0, 0.04) 0 -3px 5px;
    cursor: grabbing !important;
    padding: 2rem;
    border-radius: 10px;
    background: #f4f6f7;
  }

  &.animated {
    animation: animate 500ms ease forwards;
  }

  &&.delete {
    animation: deleteAnimate 500ms ease forwards !important;
  }

  &.dragging {
    opacity: 0;
  }

  @keyframes animate {
    0% {
      opacity: 0;
      max-height: 0;
    }
    100% {
      opacity: 1;
      max-height: 700px;
    }
  }

  @keyframes deleteAnimate {
    0% {
      opacity: 1;
      max-height: 1000px;
      margin-bottom: 2rem;
    }
    100% {
      opacity: 0;
      max-height: 0;
      margin-bottom: 0;
    }
  }
`;
